import React from 'react';
import { X, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDeleteTemplateMutation } from '@/client/api/whatsapp/template.mutation';

interface DeleteTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  templateName: string;
  onDeleteSuccess?: () => void;
}

const DeleteTemplateModal: React.FC<DeleteTemplateModalProps> = ({
  open,
  onOpenChange,
  templateId,
  templateName,
  onDeleteSuccess
}) => {
  const deleteMutation = useDeleteTemplateMutation();

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ id: templateId });
      onDeleteSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-50 transition-all duration-300 ${
        open ? 'bg-black/50 backdrop-blur-sm' : 'bg-transparent pointer-events-none'
      }`}
      onClick={() => onOpenChange(false)}
    >
      <div 
        className={`absolute inset-0 flex items-center justify-center p-4 transition-all duration-300 transform ${
          open ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-w-md w-full max-h-[90vh] overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
          {/* Header */}
          <div className="p-6 pb-4 border-b border-gray-100">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-xl border border-red-100">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Delete Template
                  </h2>
                  <p className="text-sm text-gray-500">
                    This action cannot be undone
                  </p>
                </div>
              </div>
              <button
                onClick={() => onOpenChange(false)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-xl border border-red-100 mb-6">
              <div className="flex items-start gap-3">
                <Trash2 className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900 mb-1">
                    Are you sure you want to delete &quot;{templateName}&quot;?
                  </p>
                  <p className="text-sm text-gray-600">
                    This template and all its data will be permanently removed. This action cannot be reversed.
                  </p>
                </div>
              </div>
            </div>

            {deleteMutation.isPending && (
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 border border-blue-100 p-3 rounded-lg mb-4">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
                Deleting template...
              </div>
            )}

            {deleteMutation.isError && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 p-3 rounded-lg mb-4">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {deleteMutation.error?.message || 'Failed to delete template'}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6 pt-2 border-t border-gray-100 bg-gray-50">
            <div className="flex items-center gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={deleteMutation.isPending}
                className="px-4 h-10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-6 h-10 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium"
              >
                {deleteMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Template
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteTemplateModal;